import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useToast } from '@kollez/design-system';

/** /checkout — direct port of kollez/pages/public/checkout.html. Live proforma-invoice preview. */
type Plan = 'college' | 'group' | 'assess';
const PRICE: Record<Plan, number> = { college: 240000, group: 180000, assess: 60000 };
const NAME: Record<Plan, string> = { college: 'College plan', group: 'Group plan', assess: 'Assessments only' };

function formatINR(n: number) { return n.toLocaleString('en-IN', { maximumFractionDigits: 0 }); }

export function CheckoutPage() {
  const [params] = useSearchParams();
  const toast = useToast();
  const initialPlan = (params.get('plan') as Plan | null) ?? 'group';
  const [plan, setPlan] = useState<Plan>(PRICE[initialPlan] ? initialPlan : 'group');
  const [colleges, setColleges] = useState(8);
  const [term, setTerm] = useState<'1' | '3'>('1');
  const [gov, setGov] = useState(false);
  const [addons, setAddons] = useState({ onb: false, wa: false, ai: false });
  const [gstin, setGstin] = useState('37AABTS4412K1Z2');
  const [legal, setLegal] = useState('');
  const [addr, setAddr] = useState('');

  const lines = useMemo(() => {
    const base = PRICE[plan] * (plan === 'assess' && colleges > 1 ? colleges : plan === 'group' ? colleges : 1);
    const items = [{ desc: `${NAME[plan]} · ${colleges} college${colleges > 1 ? 's' : ''} · Year 1`, sac: '998319', amount: base }];
    if (addons.onb) items.push({ desc: 'Onboarding done for you (one-time)', sac: '998311', amount: 50000 });
    if (addons.wa) items.push({ desc: 'Your own WhatsApp sender · 1 year', sac: '998319', amount: 12000 });
    if (addons.ai) items.push({ desc: 'More Nexie (5,000 queries/mo) · 1 year', sac: '998319', amount: 24000 });
    return items;
  }, [plan, colleges, addons]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, l) => s + l.amount, 0);
    const govDiscount = gov ? Math.round(subtotal * 0.15) : 0;
    const termDiscount = term === '3' ? Math.round((subtotal - govDiscount) * 0.10) : 0;
    const net = subtotal - govDiscount - termDiscount;
    const gst = Math.round(net * 0.18);
    return { subtotal, govDiscount, termDiscount, net, gst, total: net + gst };
  }, [lines, gov, term]);

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 20px 64px' }}>
      <style>{`
        .co { display: grid; gap: 24px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .co { grid-template-columns: minmax(0,1fr) minmax(0,560px); } .co-doc { position: sticky; top: 20px; } }
        .pl { display: grid; gap: 4px; padding: 14px; border: 1px solid rgb(var(--line-secondary)); border-radius: 10px; background: rgb(var(--bg-primary)); cursor: pointer; }
        .pl.active { border-color: rgb(var(--ink-accent)); box-shadow: 0 0 0 1px rgb(var(--ink-accent)); background: rgb(var(--bg-info) / .45); }
        .inv { background: #fff; color: #1a1d2b; border: 1px solid rgb(var(--line-tertiary)); border-radius: 4px; box-shadow: var(--shadow-md); padding: 28px 30px; font-size: 12.5px; line-height: 1.5; }
        .inv h3 { font-family: "Newsreader", Georgia, serif; font-size: 22px; font-weight: 500; margin: 0; }
        .inv table { width: 100%; border-collapse: collapse; margin-top: 14px; }
        .inv th { text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; color: #5b6070; border-bottom: 1px solid #1a1d2b; padding: 6px 0; }
        .inv td { padding: 6px 0; border-bottom: 1px solid #e3e6ee; vertical-align: top; }
        .inv .r { text-align: right; }
        .inv .tot td { border-bottom: 0; font-weight: 700; font-size: 14px; padding-top: 10px; }
        .stepno { width: 26px; height: 26px; border-radius: 50%; background: rgb(var(--ink-accent)); color: #fff; display: inline-grid; place-items: center; font-size: .8125rem; font-weight: 700; flex-shrink: 0; }
        @media print {
          body * { visibility: hidden !important; }
          .co-doc, .co-doc * { visibility: visible !important; }
          .co-doc { position: absolute !important; inset: 0 !important; box-shadow: none !important; padding: 0 !important; }
          .co-doc .btn { display: none !important; }
        }
      `}</style>

      <div className="co">
        <div className="grid gap-5 min-w-0">
          <div className="page-header m-0">
            <div className="ph-text">
              <Link className="link text-sm" to="/pricing">← All plans</Link>
              <h1 className="h-page mt-2">Get your quote</h1>
              <p className="subhead">Build your plan and we'll issue a proforma invoice your finance office can raise a purchase order against. Nothing is charged now.</p>
            </div>
          </div>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section flex items-center gap-2"><span className="stepno">1</span>Plan</h2>
            <div className="grid gap-2 sm:grid-cols-3">
              {(['college', 'group', 'assess'] as const).map((p) => (
                <label key={p} className={`pl ${plan === p ? 'active' : ''}`}>
                  <input type="radio" name="plan" className="sr-only" checked={plan === p} onChange={() => setPlan(p)} />
                  <b>{p === 'college' ? 'College' : p === 'group' ? 'Group' : 'Assessments only'}</b>
                  <span className="helper">₹{formatINR(PRICE[p])} per college{p === 'group' ? ' · 5+' : ''}</span>
                </label>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="grid gap-1.5"><span className="label">Colleges</span><input className="input" type="number" min={1} value={colleges} onChange={(e) => setColleges(Math.max(1, +e.target.value))} /></label>
              <label className="grid gap-1.5"><span className="label">Term</span>
                <select className="select" value={term} onChange={(e) => setTerm(e.target.value as '1' | '3')}>
                  <option value="1">1 year</option>
                  <option value="3">3 years · 10% off</option>
                </select>
              </label>
              <label className="grid gap-1.5"><span className="label">Starts</span><input className="input" type="date" defaultValue="2026-11-01" /></label>
            </div>
            <div className="grid gap-2">
              <span className="label">Add-ons</span>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" checked={addons.onb} onChange={(e) => setAddons((a) => ({ ...a, onb: e.target.checked }))} /> Onboarding done for you — ₹50,000 one-time</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" checked={addons.wa} onChange={(e) => setAddons((a) => ({ ...a, wa: e.target.checked }))} /> Your own WhatsApp sender — ₹12,000 a year</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" checked={addons.ai} onChange={(e) => setAddons((a) => ({ ...a, ai: e.target.checked }))} /> More Nexie (5,000 questions a month) — ₹24,000 a year</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="check" checked={gov} onChange={(e) => setGov(e.target.checked)} /> We're a government or aided institution (15% off, proof needed)</label>
            </div>
          </section>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section flex items-center gap-2"><span className="stepno">2</span>Who we invoice</h2>
            <label className="grid gap-1.5">
              <span className="label">GSTIN</span>
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="15 characters" value={gstin} onChange={(e) => setGstin(e.target.value)} />
                <button className="btn btn-outline" type="button" onClick={() => { setLegal('Sri Vasavi Educational Society'); setAddr('12-4, Bhimavaram, West Godavari, AP 534202'); toast.show({ message: 'Found on GST portal.' }); }}>Look up</button>
              </div>
              <span className="helper">We fill in the legal name and address from the GST portal.</span>
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5 sm:col-span-2"><span className="label">Legal name</span><input className="input" value={legal} onChange={(e) => setLegal(e.target.value)} placeholder="As on GST registration" /></label>
              <label className="grid gap-1.5 sm:col-span-2"><span className="label">Billing address</span><input className="input" value={addr} onChange={(e) => setAddr(e.target.value)} /></label>
              <label className="grid gap-1.5"><span className="label">Accounts email</span><input className="input" type="email" placeholder="accounts@institution.edu.in" /></label>
              <label className="grid gap-1.5"><span className="label">Purchase order</span>
                <select className="select">
                  <option>We'll raise one against this proforma</option>
                  <option>We already have a PO number</option>
                  <option>Our trust pays without a PO</option>
                </select>
              </label>
            </div>
          </section>

          <section className="card card-pad grid gap-4">
            <h2 className="h-section flex items-center gap-2"><span className="stepno">3</span>You</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5"><span className="label">Your name</span><input className="input" defaultValue="Dr. K. Venkatesh" /></label>
              <label className="grid gap-1.5"><span className="label">Designation</span><input className="input" defaultValue="Director, Placements" /></label>
              <label className="grid gap-1.5"><span className="label">Work email</span><input className="input" type="email" defaultValue="director@svgroup.edu.in" /></label>
              <label className="grid gap-1.5"><span className="label">Mobile</span>
                <div className="input-group"><span className="input-addon">+91</span><input className="input" inputMode="numeric" defaultValue="98480 12345" /></div>
              </label>
            </div>
          </section>

          <div className="card card-pad grid gap-3">
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-primary btn-lg" onClick={() => toast.show({ message: 'Account created. Proforma sent to your accounts email.' })}>Create account and send proforma</button>
              <button className="btn btn-outline btn-lg" onClick={() => toast.show({ message: 'Proforma emailed.' })}>Just email me the proforma</button>
            </div>
            <p className="helper m-0">Your account opens straight away on a free pilot for one college while your PO is processed. Everything carries over when the plan starts.</p>
          </div>
        </div>

        <aside className="co-doc grid gap-2">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Proforma invoice · preview</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => window.print()}
              title="Opens your browser's print dialog — Save as PDF from there."
            ><Download size={12} />Print / Save PDF</button>
          </div>
          <div className="inv">
            <div className="flex justify-between gap-4 flex-wrap">
              <div>
                <h3>Proforma invoice</h3>
                <div style={{ color: '#5b6070' }}>PI/26-27/0418 · 17 Sept 2026 · valid 30 days</div>
              </div>
              <div className="r">
                <b>Sonido Labs Pvt. Ltd.</b>
                <div style={{ color: '#5b6070' }}>Hyderabad, Telangana<br />GSTIN 36AAXCS0000X1Z0</div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#5b6070', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>Bill to</div>
              <b>{legal || 'Legal name'}</b>
              <div style={{ color: '#5b6070' }}>{addr || 'Address'}</div>
              <div style={{ color: '#5b6070' }}>GSTIN {gstin || '—'} · Place of supply Andhra Pradesh</div>
            </div>
            <table>
              <thead><tr><th>Description</th><th className="r">SAC</th><th className="r">Amount (₹)</th></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.desc}>
                    <td>{l.desc}</td>
                    <td className="r" style={{ color: '#5b6070' }}>{l.sac}</td>
                    <td className="r">{formatINR(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={2} className="r" style={{ color: '#5b6070' }}>Subtotal</td><td className="r">{formatINR(totals.subtotal)}</td></tr>
                {totals.govDiscount > 0 && <tr><td colSpan={2} className="r" style={{ color: '#5b6070' }}>Government / aided (15%)</td><td className="r">−{formatINR(totals.govDiscount)}</td></tr>}
                {totals.termDiscount > 0 && <tr><td colSpan={2} className="r" style={{ color: '#5b6070' }}>3-year term (10%)</td><td className="r">−{formatINR(totals.termDiscount)}</td></tr>}
                <tr><td colSpan={2} className="r" style={{ color: '#5b6070' }}>GST @ 18%</td><td className="r">{formatINR(totals.gst)}</td></tr>
                <tr className="tot"><td colSpan={2} className="r">Total (₹)</td><td className="r">{formatINR(totals.total)}</td></tr>
              </tfoot>
            </table>
            <div style={{ marginTop: 14, color: '#5b6070' }}>
              Term {term === '3' ? '3 years' : '1 year'}. Payment by NEFT/RTGS within 30 days of the tax invoice. Bank details appear on the final invoice. This is not a tax invoice.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
